import { Modal } from '@liveauctioneers/hammer-ui-core/modal';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import { showAppDownloadPromoAfterLeadingBidExitAtom } from '@/atoms/appDownloadPromoAtoms';
import { DownloadMobilePromo } from '@/components/Modals/DownloadMobilePromo';
import { appDownloadPromoComposableModalClassName } from '@/components/Modals/DownloadMobilePromo/constants';
import { useAppDownloadPromptSuppression } from '@/hooks/useAppDownloadPromptSuppression';
import {
    AppDownloadPromptTrigger,
    useTrackAppDownloadPromptViewed,
} from '@/utils/amplitude/analytics/notifications/trackAppDownloadPromptViewed.analytics';
import { AppDownloadSource } from '@/utils/urls/appDownloadRedirectUrl';

/**
 * Renders the LAAC-1674 app download promo in its own modal after the absentee leading-bidder
 * page closes (see useAbsenteeBidPages hideModal + atom), so it does not stack with the
 * “You're the Leading Bidder” content.
 */
export const AppDownloadPromoAfterLeadingBidHost = () => {
    const [open, setOpen] = useAtom(showAppDownloadPromoAfterLeadingBidExitAtom);
    const isMobile = useWindowIsMobile();
    const { dismissAppDownloadPrompt } = useAppDownloadPromptSuppression();
    const trackAppDownloadPromptViewed = useTrackAppDownloadPromptViewed();
    const hasTrackedAppDownloadPromptViewed = useRef(false);

    const close = useCallback(() => {
        dismissAppDownloadPrompt();
        setOpen(false);
    }, [dismissAppDownloadPrompt, setOpen]);

    useEffect(() => {
        if (!open) {
            hasTrackedAppDownloadPromptViewed.current = false;
            return;
        }

        if (!hasTrackedAppDownloadPromptViewed.current) {
            trackAppDownloadPromptViewed({ trigger: AppDownloadPromptTrigger.LeadingBidder });
            hasTrackedAppDownloadPromptViewed.current = true;
        }
    }, [open, trackAppDownloadPromptViewed]);

    if (!open) {
        return null;
    }

    return (
        <Modal
            className={appDownloadPromoComposableModalClassName}
            closeModalOnBackdropClick
            handleClose={close}
            preventScrollWhileOpen
            show
            type="composable"
        >
            <DownloadMobilePromo
                device={isMobile ? 'mobile' : 'desktop'}
                onClose={close}
                onGetApp={close}
                source={AppDownloadSource.LeadingBidder}
            />
        </Modal>
    );
};
